/**
 * Favourite careers rail — v1 had this pinned to the sidebar, but the pins
 * only ever lived in `localStorage`, so they disappeared on a second
 * device. They are account-backed now (`saved_careers`); see
 * `src/lib/savedCareers.ts` for the merge-on-sign-in behaviour.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuth } from "@/contexts/AuthContext";
import { fetchSavedCareers, type SavedCareer } from "@/lib/savedCareers";

export default function FavouriteCareers() {
  const { user } = useAuth();
  const [rows, setRows] = useState<SavedCareer[] | null>(null);

  useEffect(() => {
    let active = true;
    void fetchSavedCareers(user?.id ?? null).then((r) => {
      if (active) setRows(r);
    });
    return () => {
      active = false;
    };
  }, [user]);

  return (
    <section className="calm-card p-6 sm:p-8">
      <div className="flex items-center justify-between gap-4">
        <h3 className="font-display text-sm font-semibold uppercase tracking-[0.14em] text-muted-foreground">
          Favourite careers
        </h3>
        {rows && rows.length > 0 && (
          <Button asChild variant="ghost" className="h-10 rounded-2xl px-4 text-sm">
            <Link to="/careers">Explore more</Link>
          </Button>
        )}
      </div>

      {rows === null ? (
        <div className="mt-5 flex gap-3 overflow-hidden">
          <Skeleton className="h-24 w-48 shrink-0 rounded-2xl" />
          <Skeleton className="h-24 w-48 shrink-0 rounded-2xl" />
        </div>
      ) : rows.length === 0 ? (
        <div className="calm-card-quiet mt-5 flex items-start gap-3 p-5">
          <span className="grid h-11 w-11 shrink-0 place-items-center rounded-2xl bg-celebrate/12">
            <Star className="h-5 w-5 text-celebrate" aria-hidden="true" />
          </span>
          <div>
            <p className="text-sm font-medium">Nothing pinned yet</p>
            <p className="mt-1 text-sm text-muted-foreground">
              Save a role from your report and it'll wait for you here — on any device you sign in on.
            </p>
          </div>
        </div>
      ) : (
        <ul className="mt-5 -mx-1 flex gap-3 overflow-x-auto px-1 pb-2 snap-x">
          {rows.slice(0, 12).map((c) => (
            <li key={c.ciId} className="w-56 shrink-0 snap-start">
              <Link
                to={`/careers/${encodeURIComponent(c.ciId)}`}
                className="calm-card-quiet block h-full rounded-2xl p-4 transition-colors
                  hover:border-primary/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              >
                <Star className="h-4 w-4 text-celebrate" aria-hidden="true" />
                <p className="font-display mt-3 text-sm font-semibold leading-snug">{c.title}</p>
                {c.family && (
                  <p className="mt-1 truncate text-xs text-muted-foreground">{c.family}</p>
                )}
              </Link>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
